// treeRules.js – Gemeinsame Regeln für das Bearbeiten des Stammbaums
//
//  - buildRelationships: Formular-Beziehungen → Relationship-Objekte
//    (eine Person hat nie mehr als 2 Elternteile)
//  - checkTreeLimits:    prüft Generationslimit und Grösse der obersten Generation
// Wie Beziehungen gespeichert sind (Liste „relationships“):
//   { id, type: 'parent-child', from: Elternteil, to: Kind }
//   { id, type: 'partner',      from, to }   aktuelles Paar
//   { id, type: 'ex-partner',   from, to }   früheres Paar
// uuidv4() erzeugt eine weltweit eindeutige Kennung für jede neue Beziehung.
import { v4 as uuidv4 } from 'uuid';
import { assignGenerations } from './treeLayout';

// Grenzen des Stammbaums: höchstens 2 Eltern pro Person, höchstens 4 Generationen
// (passt auf die Karten und ins Farbsystem), oben höchstens 2 Paare/Einzelpersonen.
export const MAX_PARENTS        = 2;
export const MAX_GENERATIONS    = 4;
export const MAX_TOP_GEN_UNITS  = 2; // max. 2 Partnereinheiten (Paare oder Einzelne) in Gen0

/** Anzahl Elternteile einer Person */
export function countParents(personId, relationships) {
  return new Set(
    relationships
      .filter(r => r.type === 'parent-child' && r.to === personId)
      .map(r => r.from)
  ).size;
}

// ── Formular-Beziehungen → Relationship-Objekte ─────────────────────────
// Vorschlag für den zweiten Elternteil eines neuen Kindes von personId:
// der aktuelle Partner – oder, wenn es keinen gibt, der einzige Expartner
// (z.B. Kind von Test Test → zweiter Elternteil Anias). Sonst leer.
export function defaultSecondParent(personId, relationships) {
  const other = r => (r.from === personId ? r.to : r.from);
  const cur = relationships.filter(r => r.type === 'partner' && (r.from === personId || r.to === personId));
  if (cur.length) return other(cur[0]);
  const exs = relationships.filter(r => r.type === 'ex-partner' && (r.from === personId || r.to === personId));
  return exs.length === 1 ? other(exs[0]) : '';
}

// Übersetzt die Angaben aus dem Formular „Person hinzufügen/bearbeiten“
// (z.B. „Kind von Anna“) in gespeicherte Beziehungen.
//   personId     = die Person, die gerade bearbeitet wird
//   rels         = Liste aus dem Formular: { relationType, relatedTo, secondParent }
//   existingRels = alle bisherigen Beziehungen
export function buildRelationships(personId, rels, existingRels) {
  const result = [];
  rels.forEach(rel => {
    if (!rel || !rel.relationType || !rel.relatedTo) return;
    const { relationType, relatedTo, secondParent } = rel;

    if (relationType === 'child-of') {
      result.push({ id: uuidv4(), type: 'parent-child', from: relatedTo, to: personId });
      if (secondParent && secondParent !== '__none__') {
        // Explizit gewählter zweiter Elternteil
        result.push({ id: uuidv4(), type: 'parent-child', from: secondParent, to: personId });
      } else if (!secondParent) {
        // Leeres Feld = Auto-Detect: Partner (oder einziger Expartner) des ersten Elternteils
        const sp = defaultSecondParent(relatedTo, existingRels);
        if (sp) result.push({ id: uuidv4(), type: 'parent-child', from: sp, to: personId });
      }
      // '__none__' → kein zweiter Elternteil, kein Auto-Detect
    // „Elternteil von“: Der Partner dieser Person wird automatisch zweiter Elternteil.
    } else if (relationType === 'parent-of') {
      result.push({ id: uuidv4(), type: 'parent-child', from: personId, to: relatedTo });
      // Automatisch Partner als zweiter Elternteil hinzufügen
      existingRels
        .filter(r => r.type === 'partner' && (r.from === personId || r.to === personId))
        .map(r => r.from === personId ? r.to : r.from)
        .forEach(pid => result.push({ id: uuidv4(), type: 'parent-child', from: pid, to: relatedTo }));
    // „Geschwister von“: bekommt dieselben Eltern wie das Geschwister.
    } else if (relationType === 'sibling-of') {
      existingRels
        .filter(r => r.type === 'parent-child' && r.to === relatedTo)
        .forEach(r => result.push({ id: uuidv4(), type: 'parent-child', from: r.from, to: personId }));
    } else if (relationType === 'partner-of') {
      result.push({ id: uuidv4(), type: 'partner',    from: relatedTo, to: personId });
    } else if (relationType === 'ex-partner-of') {
      result.push({ id: uuidv4(), type: 'ex-partner', from: relatedTo, to: personId });
    }
  });

  // ── Schutz: höchstens 2 Elternteile pro Person, keine Duplikate ─────
  // Bestehende Eltern zählen zuerst; neue Eltern nur solange Platz ist.
  // Explizit gewählte Eltern stehen im Array vor automatisch ergänzten Partnern.
  const parentsOf = new Map();
  existingRels.forEach(r => {
    if (r.type !== 'parent-child') return;
    if (!parentsOf.has(r.to)) parentsOf.set(r.to, new Set());
    parentsOf.get(r.to).add(r.from);
  });

  return result.filter(r => {
    if (r.type !== 'parent-child') return true;
    if (r.from === r.to) return false;
    if (!parentsOf.has(r.to)) parentsOf.set(r.to, new Set());
    const set = parentsOf.get(r.to);
    if (set.has(r.from)) return false;          // Duplikat
    if (set.size >= MAX_PARENTS) return false;  // schon 2 Elternteile
    set.add(r.from);
    return true;
  });
}

/** Anzahl Partnereinheiten (partner-connected Komponenten) in einer Liste von IDs */
// Zählt, wie viele „Partner-Gruppen“ es in einer Liste gibt (Paare zählen als eine
// Einheit, Einzelpersonen auch). Technik „Union-Find“: Jede Person zeigt auf einen
// Vertreter ihrer Gruppe; find() sucht den Vertreter, union() verbindet zwei Gruppen.
function countUnits(ids, relationships) {
  const idSet = new Set(ids);
  const parent = new Map(ids.map(id => [id, id]));
  const find = (id) => {
    if (parent.get(id) !== id) parent.set(id, find(parent.get(id)));
    return parent.get(id);
  };
  const union = (a, b) => {
    const ra = find(a), rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };
  relationships.forEach(r => {
    if (r.type !== 'partner' && r.type !== 'ex-partner') return;
    if (idSet.has(r.from) && idSet.has(r.to)) union(r.from, r.to);
  });
  return new Set(ids.map(id => find(id))).size;
}

// Anzahl Personen in der längsten Eltern→Kind-Kette (1 = nur eine Generation)
function longestChain(persons, relationships) {
  const kids = new Map();
  relationships.forEach(r => {
    if (r.type !== 'parent-child') return;
    if (!kids.has(r.from)) kids.set(r.from, []);
    kids.get(r.from).push(r.to);
  });
  // memo = bereits berechnete Tiefen (spart Rechenzeit), visiting = schützt vor
  // Endlosschleifen, falls die Daten versehentlich einen Kreis enthalten.
  const memo = new Map();
  const visiting = new Set();
  const depth = (id) => {
    if (memo.has(id)) return memo.get(id);
    if (visiting.has(id)) return 0; // Zyklus-Schutz
    visiting.add(id);
    let d = 1;
    (kids.get(id) || []).forEach(c => { d = Math.max(d, 1 + depth(c)); });
    visiting.delete(id);
    memo.set(id, d);
    return d;
  };
  let max = 0;
  persons.forEach(p => { max = Math.max(max, depth(p.id)); });
  return max;
}

// ── Limits prüfen ────────────────────────────────────────────────────────
// Rückgabe: null (ok) | 'generations' | 'topGeneration'
export function checkTreeLimits(persons, relationships) {
  if (!persons.length) return null;
  // Generationen = längste Eltern→Kind-Kette (robuster als die Layout-Generationen,
  // die bei Querverbindungen zwischen Familien inkonsistent sein können)
  const chain = longestChain(persons, relationships);
  if (chain > MAX_GENERATIONS) return 'generations';

  const genMap = assignGenerations(persons, relationships);
  const gens   = [...genMap.values()];
  const minGen = Math.min(...gens);
  const span   = Math.max(...gens) - minGen;

  if (span > MAX_GENERATIONS - 1) return 'generations';

  // Gen0: immer höchstens 2 Partnereinheiten (Paare oder Einzelpersonen)
  const topIds = [...genMap.entries()].filter(([, g]) => g === minGen).map(([id]) => id);
  if (countUnits(topIds, relationships) > MAX_TOP_GEN_UNITS) return 'topGeneration';

  return null;
}

// ── Eltern für Partner weiter unten im Stammbaum ─────────────────────────
// Partner und Expartner ab der dritten Generation (z.B. der Partner einer Enkelin)
// dürfen keine Eltern bekommen. Ihre Familie wäre eine dritte oberste Familie mitten
// im Stammbaum – dafür gibt es keine eindeutige Lösung mit Farben und Symbolen.
// Ihre Familie kann stattdessen als Erweiterung erfasst werden (utils/extension.js).
// Liefert die Personen, die (noch) keine Eltern haben und ab Generation 3 stehen.
export function parentsNotAllowed(persons, relationships) {
  const res = new Set();
  if (!persons.length) return res;
  const genMap = assignGenerations(persons, relationships);
  const minGen = Math.min(...genMap.values());
  const hasParent = new Set(relationships.filter(r => r.type === 'parent-child').map(r => r.to));
  persons.forEach(p => {
    if (!hasParent.has(p.id) && (genMap.get(p.id) ?? minGen) - minGen >= 2) res.add(p.id);
  });
  return res;
}

/**
 * Prüft eine geplante Änderung. Blockiert nur, wenn die Änderung ein Limit
 * NEU verletzt (bestehende Altlasten blockieren nicht jede weitere Bearbeitung).
 */
export function checkChange(oldPersons, oldRels, newPersons, newRels, sim = null) {
  // Geschwister nur bei Personen, deren Eltern schon im Stammbaum sind.
  if (sim && sim.siblingWithoutParents) return 'siblingNoParents';
  // Eingeheiratete Personen ab der dritten Generation dürfen keine Eltern bekommen.
  const blocked = parentsNotAllowed(oldPersons, oldRels);
  const gotParents = newRels.some(r => r.type === 'parent-child' && blocked.has(r.to));
  if (gotParents) return 'partnerParents';
  const after = checkTreeLimits(newPersons, newRels);
  if (!after) return null;
  const before = checkTreeLimits(oldPersons, oldRels);
  return after === before ? null : after;
}

// „Geschwister von X“ geht nur, wenn X schon Eltern im Stammbaum hat – sonst gäbe es
// keine gemeinsamen Eltern, und die neue Person hinge nirgends.
function siblingWithoutParents(relationsData, relationships) {
  return (relationsData || []).some(rel => rel && rel.relationType === 'sibling-of' && rel.relatedTo &&
    !relationships.some(r => r.type === 'parent-child' && r.to === rel.relatedTo));
}

/** Simuliert "Person hinzufügen" → { persons, relationships } */
// Diese beiden Funktionen bauen den Stammbaum so, wie er NACH einer Änderung
// aussehen würde – ohne etwas zu speichern. Damit prüft checkChange vorher,
// ob die Änderung erlaubt ist (sonst erscheint das Limit-Fenster).
export function simulateAdd(persons, relationships, formData, relationsData) {
  const tempId = '__temp_check__';
  const newRels = buildRelationships(tempId, relationsData || [], relationships);
  return {
    siblingWithoutParents: siblingWithoutParents(relationsData, relationships),
    persons:       [...persons, { id: tempId, ...formData }],
    relationships: [...relationships, ...newRels],
  };
}

/** Simuliert "Person bearbeiten" → { persons, relationships } */
export function simulateEdit(persons, relationships, personId, formData, relationsData) {
  let rels = relationships;
  if (Array.isArray(relationsData)) {
    rels = rels.filter(r => r.from !== personId && r.to !== personId);
    rels = [...rels, ...buildRelationships(personId, relationsData, rels)];
  }
  return {
    siblingWithoutParents: siblingWithoutParents(relationsData, relationships),
    persons: persons.map(p => p.id === personId ? { ...p, ...formData } : p),
    relationships: rels,
  };
}
